import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon, XIcon, Loader2Icon } from 'lucide-react';
export function CTASlide({
  headline,
  primaryButtonText,
  secondaryButtonText,
  onBack
}: {
  headline: string;
  primaryButtonText: string;
  secondaryButtonText: string;
  onBack: () => void;
}) {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const openEmbed = (url: string) => {
    setIframeLoaded(false);
    setEmbedUrl(url);
  };
  const closeEmbed = () => {
    setEmbedUrl(null);
    setIframeLoaded(false);
  };
  const displayUrl = embedUrl ? embedUrl.replace(/^https?:\/\/www\./, '').replace(/\/$/, '') : '';
  return <div className="flex flex-col h-full relative z-10 bg-[#0B1623] text-white overflow-hidden" data-id="element-6">
      <AnimatePresence mode="wait" data-id="element-7">
        {embedUrl ? <motion.div key="embed" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.3
      }} className="flex flex-col h-full" data-id="element-8">
            {/* Embed header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0 bg-[#0B1623]" data-id="element-9">
              <button onClick={closeEmbed} className="p-2 -ml-2 text-slate-400 hover:text-white transition-colors" aria-label="Close" data-id="element-10">
                <XIcon className="w-5 h-5" data-id="element-11" />
              </button>
              <div className="flex-1 text-center" data-id="element-12">
                <p className="text-xs text-slate-400 truncate px-4" data-id="element-13">
                  {displayUrl}
                </p>
              </div>
              <div className="w-9" data-id="element-14" />
            </div>

            {/* Loading state */}
            {!iframeLoaded && <div className="flex-1 flex items-center justify-center" data-id="element-15">
                <Loader2Icon className="w-6 h-6 text-[#4B9FE1] animate-spin" data-id="element-16" />
              </div>}

            {/* Iframe */}
            <iframe src={embedUrl} className={`flex-1 w-full border-0 bg-white ${iframeLoaded ? 'block' : 'hidden'}`} onLoad={() => setIframeLoaded(true)} title="Minerva Imaging" sandbox="allow-scripts allow-same-origin allow-popups allow-forms" data-id="element-17" />
          </motion.div> : <motion.div key="cta" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="flex flex-col h-full px-6 py-12 justify-center" data-id="element-18">
            {/* Lab background with gradient overlay */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-19">
              <img src="https://cdn.magicpatterns.com/uploads/hF7rvjEq9antGAY8GqPuMH/Screenshot_2026-03-04_at_16.46.47.png" className="absolute inset-0 w-full h-full object-cover opacity-20" alt="" data-id="element-20" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1623] via-[#0B1623]/80 to-[#0B1623]/40" data-id="element-21" />
            </div>

            {/* Back button — top left */}
            <button onClick={onBack} className="absolute top-6 left-6 z-20 p-2 -ml-2 text-slate-500 hover:text-white transition-colors" aria-label="Go back" data-id="element-22">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-23" />
            </button>

            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="text-center relative z-10" data-id="element-24">
              <div className="bg-white px-6 py-4 rounded-2xl inline-block shadow-xl mb-10 relative" data-id="element-25">
                <div className="absolute inset-0 rounded-2xl border border-[#4B9FE1]/30 animate-ping opacity-20" data-id="element-26" />
                <img src="https://cdn.magicpatterns.com/uploads/ifejLUcSApynCwwp5oN3Zr/Screenshot_2026-03-04_at_16.40.29.png" className="h-10 object-contain" alt="Minerva" data-id="element-27" />
              </div>

              <h2 className="text-3xl md:text-4xl font-light mb-16 leading-tight text-white" data-id="element-28">
                {headline}
              </h2>

              <div className="space-y-4 w-full max-w-sm mx-auto" data-id="element-29">
                <button onClick={() => openEmbed('https://www.minervaimaging.com/publications/')} className="w-full py-4 px-6 bg-[#4B9FE1] text-white rounded-xl font-medium flex items-center justify-center hover:bg-[#3A8BD0] transition-colors group focus:outline-none focus:ring-2 focus:ring-[#4B9FE1] focus:ring-offset-2 focus:ring-offset-[#0B1623]" data-id="element-30">
                  {primaryButtonText}
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-31" />
                </button>

                <button onClick={() => openEmbed('https://www.minervaimaging.com/about-us/')} className="w-full py-4 px-6 bg-transparent border border-white/20 text-slate-300 rounded-xl font-medium flex items-center justify-center hover:bg-white/5 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-[#0B1623]" data-id="element-32">
                  {secondaryButtonText}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}